/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/qaItpScope.ts
 * FILE_ID: JOBHUB-QA-ITP-SCOPE-001
 * PURPOSE: Resolve ITP contents from the live selection, then the highlight.
 * LANDMINE: One-arg isSelected(item.id) always misses. Use the two-arg form.
 */
import { areaItems, checklistGroups } from '../../qa/qaApiHelpers'
import type { QaArea, QaAreaExecution, QaChecklistInstance, QaFloor, QaItemInstance } from '../../qa/qaApi'
import { levelsOf } from './qaTabSelectors'

export type QaItpScope = 'areas' | 'checklists' | 'checks'

export interface QaItpRequest {
  areaIds: string[]
  checklistIds: string[] | null
  itemIds: string[] | null
}

export type QaItpUi = {
  mode: 'prompt' | 'modal'
} & QaItpRequest

function idKey(ids: readonly string[] | null | undefined): string {
  if (ids == null) return '*'
  return ids.join(',')
}

/** Keep an open ITP modal on the live selection. "All" stays all. */
export function liveModalRequest(prev: QaItpUi | null, request: QaItpRequest): QaItpUi | null {
  if (!prev || prev.mode !== 'modal') return prev
  const keepAll = prev.itemIds === null && request.itemIds !== null
  const itemIds = keepAll ? null : request.itemIds
  if (
    idKey(prev.areaIds) === idKey(request.areaIds)
    && idKey(prev.checklistIds) === idKey(request.checklistIds)
    && idKey(prev.itemIds) === idKey(itemIds)
  ) return prev
  return { ...prev, ...request, mode: 'modal', itemIds }
}

export function uniqueIds(ids: readonly string[]): string[] {
  return [...new Set(ids)]
}

export function qaDeleteTargets(
  union: { areas: readonly string[]; checklists: readonly string[]; checks: readonly string[] },
  executions: readonly QaAreaExecution[],
): { items: string[]; checklists: string[]; areas: string[] } {
  const selectedAreas = new Set(union.areas)
  const selectedLists = new Set(union.checklists)
  const checklistArea = new Map<string, string>()
  const itemChecklist = new Map<string, string>()
  for (const area of executions) {
    for (const group of checklistGroups(area)) {
      checklistArea.set(group.checklist.id, area.id)
      for (const item of group.items) itemChecklist.set(item.id, group.checklist.id)
    }
  }
  return {
    items: union.checks.filter((id) => !selectedLists.has(itemChecklist.get(id) ?? '')),
    checklists: union.checklists.filter((id) => !selectedAreas.has(checklistArea.get(id) ?? '')),
    areas: [...union.areas],
  }
}

export function qaLevelDeleteTargets(
  targets: { items: string[]; checklists: string[]; areas: string[] },
  floors: readonly QaFloor[],
  areas: readonly QaArea[],
  selectedEmptyLevelIds: readonly string[],
  executions: readonly QaAreaExecution[],
): { items: string[]; checklists: string[]; areas: string[]; floors: string[] } {
  // Reused from: packages/job-hub/src/jobs/tabs/job-qa/qaTabSelectors.ts (levelsOf)
  const grouped = levelsOf([...floors], [...areas])
  const defaultId = floors[0]?.id
  const selectedAreas = new Set(targets.areas)
  const emptyPicked = new Set(selectedEmptyLevelIds)
  const floorIds: string[] = []
  const dropAreas = new Set<string>()
  for (const group of grouped) {
    const floor = group.floor
    if (!floor || floor.id === defaultId) continue
    if (group.areas.length === 0) {
      if (emptyPicked.has(floor.id)) floorIds.push(floor.id)
      continue
    }
    if (!group.areas.every((area) => selectedAreas.has(area.id))) continue
    floorIds.push(floor.id)
    for (const area of group.areas) dropAreas.add(area.id)
  }
  const dropLists = new Set<string>()
  const dropItems = new Set<string>()
  for (const area of executions) {
    if (!dropAreas.has(area.id)) continue
    for (const group of checklistGroups(area)) {
      dropLists.add(group.checklist.id)
      for (const item of group.items) dropItems.add(item.id)
    }
  }
  return {
    items: targets.items.filter((id) => !dropItems.has(id)),
    checklists: targets.checklists.filter((id) => !dropLists.has(id)),
    areas: targets.areas.filter((id) => !dropAreas.has(id)),
    floors: floorIds,
  }
}

export function qaDeleteConfirm(targets: { items: readonly string[]; checklists: readonly string[]; areas: readonly string[]; floors?: readonly string[] }): string {
  const parts: string[] = []
  const floorCount = targets.floors?.length ?? 0
  if (floorCount) parts.push(`${floorCount} level${floorCount === 1 ? '' : 's'}`)
  if (targets.areas.length) parts.push(`${targets.areas.length} area${targets.areas.length === 1 ? '' : 's'}`)
  if (targets.checklists.length) parts.push(`${targets.checklists.length} checklist${targets.checklists.length === 1 ? '' : 's'}`)
  if (targets.items.length) parts.push(`${targets.items.length} item${targets.items.length === 1 ? '' : 's'}`)
  return `Delete ${parts.join(', ')}?`
}

function liveGroups(area: QaAreaExecution) {
  return checklistGroups(area, undefined)
}

function locateCheck(
  executions: readonly QaAreaExecution[],
  itemId: string,
): { area: QaAreaExecution; checklist: QaChecklistInstance; item: QaItemInstance } | null {
  for (const area of executions) {
    for (const group of liveGroups(area)) {
      const item = group.items.find((row) => row.id === itemId)
      if (item) return { area, checklist: group.checklist, item }
    }
  }
  return null
}

export function liveItpItemIdsFromSelection(
  isSelected: (scope: QaItpScope, id: string) => boolean,
  items: readonly { id: string }[],
): string[] {
  return items.filter((item) => isSelected('checks', item.id)).map((item) => item.id)
}

export function selectedIdsForScope(
  scope: QaItpScope | null,
  visibleOf: (scope: QaItpScope, ids: readonly string[]) => string[],
  executions: readonly QaAreaExecution[],
  areaIds: readonly string[],
): string[] {
  if (!scope) return []
  if (scope === 'areas') return visibleOf('areas', areaIds)
  if (scope === 'checklists') {
    return visibleOf(
      'checklists',
      executions.flatMap((area) => area.checklists.map((row) => row.id)),
    )
  }
  return visibleOf(
    'checks',
    executions.flatMap((area) => areaItems(area).map((item) => item.id)),
  )
}

/** The three pruned selection sets, one call site for both workspaces. */
export function selectedQaUnion(
  visibleOf: (scope: QaItpScope, ids: readonly string[]) => string[],
  executions: readonly QaAreaExecution[],
  areaIds: readonly string[],
  excludedChecks?: ReadonlySet<string>,
): { areas: string[]; checklists: string[]; checks: string[]; excludedChecks: string[] } {
  const visibleChecks = executions.flatMap((area) => areaItems(area).map((item) => item.id))
  return {
    areas: visibleOf('areas', areaIds),
    checklists: visibleOf(
      'checklists',
      executions.flatMap((area) => area.checklists.map((row) => row.id)),
    ),
    checks: visibleOf('checks', visibleChecks),
    excludedChecks: [...(excludedChecks ?? [])].filter((id) => visibleChecks.includes(id)),
  }
}

export function expandItpItemIds(
  request: QaItpRequest,
  executions: readonly QaAreaExecution[],
): string[] {
  if (request.itemIds) return request.itemIds
  return executions
    .filter((area) => request.areaIds.includes(area.id))
    .flatMap((area) => liveGroups(area))
    .filter((group) => !request.checklistIds || request.checklistIds.includes(group.checklist.id))
    .flatMap((group) => group.items.map((item) => item.id))
}

export function resolveItpScope(input: {
  selected: {
    areas: readonly string[]
    checklists: readonly string[]
    checks: readonly string[]
    excludedChecks?: readonly string[]
  }
  activeAreaId: string | null
  activeChecklistId: string | null
  allChecklists: boolean
  executions: QaAreaExecution[]
  selectionTouched?: boolean
}): QaItpRequest {
  const { selected, activeAreaId, activeChecklistId, allChecklists, executions, selectionTouched } = input
  const explicit = new Set(selected.checks)
  const excluded = new Set(
    (selected.excludedChecks ?? []).filter((id) => !explicit.has(id)),
  )
  const dropped = (id: string) => excluded.has(id)
  const populatedKinds =
    Number(selected.areas.length > 0)
    + Number(selected.checklists.length > 0)
    + Number(selected.checks.length > 0)

  // Mixed selection (mockup "Select" rules): the UNION of selected areas'
  // items, selected checklists' items, and individually selected checks —
  // expanded to explicit item ids so nothing selected can be dropped.
  if (populatedKinds > 1) {
    const areaIds = new Set<string>()
    const checklistIds = new Set<string>()
    const itemIds = new Set<string>()
    for (const area of executions) {
      const areaSelected = selected.areas.includes(area.id)
      for (const group of liveGroups(area)) {
        const listSelected = areaSelected || selected.checklists.includes(group.checklist.id)
        for (const item of group.items) {
          if ((listSelected || explicit.has(item.id)) && !dropped(item.id)) {
            areaIds.add(area.id)
            checklistIds.add(group.checklist.id)
            itemIds.add(item.id)
          }
        }
      }
    }
    if (itemIds.size > 0) {
      return {
        areaIds: [...areaIds],
        checklistIds: [...checklistIds],
        itemIds: [...itemIds],
      }
    }
  }

  if (selected.checks.length > 0) {
    const found = selected.checks
      .map((id) => locateCheck(executions, id))
      .filter((row): row is NonNullable<typeof row> => Boolean(row))
    if (found.length > 0) {
      return {
        areaIds: uniqueIds(found.map((row) => row.area.id)),
        checklistIds: uniqueIds(found.map((row) => row.checklist.id)),
        itemIds: found.map((row) => row.item.id),
      }
    }
  }

  if (selected.checklists.length > 0) {
    const found: { areaId: string; checklistId: string }[] = []
    for (const area of executions) {
      for (const group of liveGroups(area)) {
        if (selected.checklists.includes(group.checklist.id)) {
          found.push({ areaId: area.id, checklistId: group.checklist.id })
        }
      }
    }
    if (found.length > 0) {
      const areaIds = uniqueIds(found.map((row) => row.areaId))
      const checklistIds = uniqueIds(found.map((row) => row.checklistId))
      if (excluded.size === 0) {
        return { areaIds, checklistIds, itemIds: null }
      }
      const itemIds = executions
        .filter((area) => areaIds.includes(area.id))
        .flatMap((area) => liveGroups(area))
        .filter((group) => checklistIds.includes(group.checklist.id))
        .flatMap((group) => group.items.map((item) => item.id))
        .filter((id) => !dropped(id))
      return { areaIds, checklistIds, itemIds }
    }
  }

  if (selected.areas.length > 0) {
    const areaIds = uniqueIds([...selected.areas])
    if (excluded.size === 0) {
      return { areaIds, checklistIds: null, itemIds: null }
    }
    const itemIds = executions
      .filter((area) => areaIds.includes(area.id))
      .flatMap((area) => liveGroups(area))
      .flatMap((group) => group.items.map((item) => item.id))
      .filter((id) => !dropped(id))
    return { areaIds, checklistIds: null, itemIds }
  }

  if (selectionTouched && populatedKinds === 0) {
    return { areaIds: [], checklistIds: [], itemIds: [] }
  }

  if (!activeAreaId) return { areaIds: [], checklistIds: null, itemIds: null }
  if (allChecklists || !activeChecklistId) {
    return { areaIds: [activeAreaId], checklistIds: null, itemIds: null }
  }
  return { areaIds: [activeAreaId], checklistIds: [activeChecklistId], itemIds: null }
}

/** ITP popup Export: ticked checks win; nothing ticked means every check the popup shows. */
export function modalExportRequest(
  groups: ReadonlyArray<{ areaId: string; checklistId: string; itemIds: readonly string[] }>,
  isIncluded: (itemId: string, checklistId: string, areaId: string) => boolean,
): QaItpRequest {
  const ticked = groups.flatMap((group) => group.itemIds
    .filter((id) => isIncluded(id, group.checklistId, group.areaId))
    .map((id) => ({ ...group, id })))
  if (ticked.length > 0) {
    return {
      areaIds: uniqueIds(ticked.map((row) => row.areaId)),
      checklistIds: uniqueIds(ticked.map((row) => row.checklistId)),
      itemIds: ticked.map((row) => row.id),
    }
  }
  return {
    areaIds: uniqueIds(groups.map((group) => group.areaId)),
    checklistIds: uniqueIds(groups.map((group) => group.checklistId)),
    itemIds: groups.flatMap((group) => [...group.itemIds]),
  }
}
